import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Navigate } from 'react-router-dom';
import { useDatabase } from '@/contexts/DatabaseContext';

export default function LoginPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { login, isLoggedIn } = useDatabase();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const success = await login(email, password);
    setLoading(false);
    if (success) {
      navigate('/');
    } else {
      setError(t('invalidCredentials') || 'Email ou mot de passe incorrect.');
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-12 h-12 mx-auto rounded-xl bg-primary-500 flex items-center justify-center mb-4">
            <i className="ri-truck-line text-background-50 text-2xl" />
          </div>
          <h1 className="text-2xl font-bold text-foreground-950 font-heading">
            SILY-OBC
          </h1>
          <p className="text-sm text-foreground-500 mt-1">{t('dashboard')}</p>
        </div>

        {/* Form */}
        <div className="bg-background-50 rounded-xl border border-background-200 p-6 md:p-8">
          <h2 className="text-lg font-semibold text-foreground-900 mb-6">{t('login')}</h2>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-accent-50 text-accent-800 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-foreground-600 mb-1.5 block">
                {t('email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                placeholder="email@exemple.com"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-foreground-600 mb-1.5 block">
                {t('password')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 pr-10 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors"
                  tabIndex={-1}
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? t('loading') : t('signIn')}
            </button>
          </form>

          <p className="text-sm text-foreground-500 text-center mt-6">
            SILY-OBC — Gestion de flotte
          </p>
        </div>
      </div>
    </div>
  );
}